import React, { useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from './firebase';
import axios from 'axios';
import EnvironmentManager from '../config/EnvironmentManager';
import { useNavigate, useLocation } from 'react-router-dom';
import './VideoCourseExcelUpload.css';

import {
  Home,
  Upload,
  FileSpreadsheet,
  Download,
  AlertCircle,
  CheckCircle,
  XCircle,
  FolderOpen,
  ArrowRight,
  HelpCircle,
  ChevronRight,
  Sparkles,
  Shield,
  Users,
  Video,
  FileText,
  PlayCircle,
  FileCode,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Info,
  X,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';

const VideoCourseExcelUpload = () => {
  const auth = getAuth(app);
  const navigate = useNavigate();
  const location = useLocation();
  // 🔒 LOCK edit flow intent (do NOT rely on location.state later)
const editFlowRef = useRef({
  fromEditOptions: location.state?.fromEditOptions || false,
  updateType: location.state?.updateType || null,
  redirectTo: location.state?.redirectTo || '/MHMV-profile/video-courses'
});

  
 
  const existingCourseId = location.state?.courseId || null;
  const editMode = location.state?.editMode || false;
  const courseData = location.state?.courseData || null;
  const fromVideoUpload = location.state?.fromVideoUpload || false;
  const fromExcelUpdate = location.state?.fromExcelUpdate || false;

  const excelInputRef = useRef(null);
  const [excelFile, setExcelFile] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [authChecked, setAuthChecked] = useState(false);
  const [excelAlreadyUploaded, setExcelAlreadyUploaded] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const [showStepGuide, setShowStepGuide] = useState(!editMode && !fromVideoUpload);
  const [activeStep, setActiveStep] = useState('instructors');
  const [instructorsExpanded, setInstructorsExpanded] = useState(!editMode && !fromVideoUpload);
  const [showInstructionsBanner, setShowInstructionsBanner] = useState(!editMode && !fromVideoUpload);
  const [existingExcelFile, setExistingExcelFile] = useState(null);
  const [courseIdFromApi, setCourseIdFromApi] = useState(null);
  const [courseModules, setCourseModules] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Debug logging
  useEffect(() => {
    console.log('=== Excel Upload Page Debug ===');
    console.log('existingCourseId:', existingCourseId);
    console.log('fromVideoUpload:', fromVideoUpload);
    console.log('editMode:', editMode);
    console.log('location.state:', location.state);
    console.log('refreshTrigger:', refreshTrigger);
    
  }, [existingCourseId, fromVideoUpload, editMode, location.state, refreshTrigger]);

  
  const findExcelUrl = (courseData) => {
    if (!courseData) return null;
    
    const excelExtensions = ['.xlsx', '.xls', '.xlsm'];
    const knownFields = [
      'video_media_url', 'videoMediaUrl', 'excelFile', 'fileUrl', 
      'mediaUrl', 'courseFile', 'uploadedFile'
    ];
    
    for (const field of knownFields) {
      if (courseData[field] && excelExtensions.some(ext => 
        courseData[field].toLowerCase().includes(ext))) {
        return courseData[field];
      }
    }
   
    for (const [key, value] of Object.entries(courseData)) {
      if (typeof value === 'string' && excelExtensions.some(ext => 
        value.toLowerCase().includes(ext))) {
        return value;
      }
    }
    
    return null;
  };


  useEffect(() => {
    if (!location.hash && !editMode && !fromVideoUpload) {
      navigate('#instructions', { replace: true });
    }
  }, [location.hash, navigate, editMode, fromVideoUpload]);

 
  useEffect(() => {
    const hash = location.hash;
    
    if (editMode || fromVideoUpload || fromExcelUpdate) {
      setActiveStep('uploadExcel');
      if (hash !== '#upload') {
        navigate('#upload', { replace: true });
      }
    } else if (hash === '#upload') {
      setActiveStep('uploadExcel');
    } else {
      setActiveStep('instructors');
    }
  }, [location.hash, editMode, fromVideoUpload, fromExcelUpdate, navigate]);

  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, [auth]);


  useEffect(() => {
    if (user) {
      if (existingCourseId) {
        checkExcelStatus();
      } else if (fromVideoUpload) {
       
        const savedCourse = localStorage.getItem('lastVideoCourse');
        if (savedCourse) {
          try {
            const parsed = JSON.parse(savedCourse);
            if (parsed.courseId) {
             
              checkExcelStatusWithId(parsed.courseId);
            }
          } catch (e) {
            console.error('Error reading localStorage:', e);
          }
        }
      }
    }
  }, [user, existingCourseId, fromVideoUpload, refreshTrigger]);

  const checkExcelStatusWithId = async (courseId) => {
    if (!courseId || !user) return;
    
    setCheckingStatus(true);
    try {
      const idToken = await user.getIdToken(true);
      
      const response = await axios.get(
        `${EnvironmentManager.baseUrl}/mv-profile/video-courses/course-with-excel/${courseId}`,
        {
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );

      const courseData = response.data;
      console.log('Course data from API:', courseData);
      
     
      if (courseData.courseId) {
        setCourseIdFromApi(courseData.courseId);
      }
      
      
      const hasModules = courseData?.modules?.length > 0 || courseData?.videoModules?.length > 0;
      setExcelAlreadyUploaded(hasModules);
      
     
      if (hasModules) {
        setCourseModules(courseData.modules || courseData.videoModules || []);
      }
      
      
      if (hasModules && location.hash !== '#upload' && !editMode) {
        navigate('#upload', { replace: true });
      }

     
      const excelUrl = findExcelUrl(courseData);
      if (excelUrl) {
        const filename = excelUrl.split('/').pop() || 'course-structure.xlsx';
        setExistingExcelFile({
          name: filename,
          filePath: excelUrl,
          uploadedAt: courseData.updated_date || courseData.created_date || 'Previously uploaded',
          size: 'Unknown',
          courseId: courseData.courseId || courseId
        });
      }
      
   
      localStorage.setItem('lastVideoCourse', JSON.stringify({
        courseId: courseData.courseId || courseId,
        title: courseData.title || 'Untitled Course',
        timestamp: new Date().toISOString(),
        hasExcel: hasModules
      }));
      
    } catch (error) {
      console.error('Error checking excel status:', error);
      showMessage('error', `Failed to check course status: ${error.message}`);
    } finally {
      setCheckingStatus(false);
    }
  };

  const checkExcelStatus = async () => {
    await checkExcelStatusWithId(existingCourseId);
  };

  
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && existingCourseId && user) {
        checkExcelStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [existingCourseId, user]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const getIdToken = async () => {
    if (user) {
      try {
        return await user.getIdToken(true);
      } catch (error) {
        console.error('Error getting ID token:', error);
        return null;
      }
    }
    return null;
  };

  const handleStepClick = (stepId) => {
    if (stepId === 'uploadExcel') {
      navigate('#upload');
    } else {
      navigate('#instructions');
    }
  };

  const handleExcelFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      showMessage('error', 'Please select an Excel file (.xlsx or .xls format)');
      return;
    }

   
    if (file.size > 10 * 1024 * 1024) {
      showMessage('error', 'File size too large. Maximum 10MB allowed.');
      return;
    }

    setExcelFile(file);
    showMessage('success', `Selected: ${file.name} (${(file.size / 1024).toFixed(0)} KB)`);
  };

  const handleReplaceClick = () => {
    setReplaceMode(true);
    setExcelFile(null);
    if (excelInputRef.current) {
      excelInputRef.current.value = '';
    }
  
    setExcelAlreadyUploaded(false);
  };

  const handleCancelReplace = () => {
    if (editMode) {
      navigate('/MHMV-profile/video-courses');
    } else {
      setReplaceMode(false);
      setExcelFile(null);
      if (excelInputRef.current) {
        excelInputRef.current.value = '';
      }
    
      if (existingExcelFile) {
        setExcelAlreadyUploaded(true);
      }
      showMessage('info', 'Replace cancelled');
    }
  };

  const downloadExistingExcel = async () => {
    if (!existingExcelFile?.filePath || !user) {
      showMessage('error', 'No Excel file available to download');
      return;
    }

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        showMessage('error', 'Authentication required');
        return;
      }

      let filePath = existingExcelFile.filePath.trim();
      filePath = filePath.replace(/^[:]+/, '').trim();
      
      const downloadUrl = `${EnvironmentManager.baseUrl}/mv-profile/files/download?fileUrl=${encodeURIComponent(filePath)}`;
      
      showMessage('info', 'Preparing download...');

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
        }
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = existingExcelFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(blobUrl);
      
      showMessage('success', `Download started: ${existingExcelFile.name}`);

    } catch (error) {
      console.error('Excel download error:', error);
      showMessage('error', `Failed to download: ${error.message}`);
    }
  };

  const handleUpload = async () => {
    console.log('=== UPLOAD DEBUG START ===');
  console.log('editMode:', editMode);
  console.log('excelAlreadyUploaded:', excelAlreadyUploaded);
  console.log('replaceMode:', replaceMode);
  console.log('existingCourseId:', existingCourseId);
  console.log('courseIdFromApi:', courseIdFromApi);
  console.log('location.state:', location.state);
  console.log('=== UPLOAD DEBUG END ===');
  if (!user) {
    showMessage('error', 'Please login first');
    return;
  }

 
  if (excelAlreadyUploaded && !replaceMode && !editMode) {
    const courseIdToUse = existingCourseId || courseIdFromApi;
    if (!courseIdToUse) {
      showMessage('error', 'Course ID not found. Please refresh the page.');
      return;
    }
    
    navigate(`/MHMV-profile/upload-course-videos/${courseIdToUse}`, {
      state: { 
        editMode: editMode,
        courseId: courseIdToUse,
        fromExcelPage: true
      }
    });
    return;
  }

  // Replace mode or edit mode requires new file
  if ((replaceMode || editMode) && !excelFile) {
    showMessage('error', 'Please upload a new Excel file');
    return;
  }

  if (!excelFile) {
    showMessage('error', 'Please select an Excel file to upload');
    return;
  }

  setLoading(true);

  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Authentication failed');
    }

    const formData = new FormData();
    formData.append('excelFile', excelFile);
    formData.append('userid', user.uid);
    
    let endpoint;
    let courseIdToUse;
    
  
    const isUpdate = editMode || replaceMode || excelAlreadyUploaded;
    
    if (isUpdate) {

      courseIdToUse = existingCourseId || courseIdFromApi;
      if (!courseIdToUse) {
        throw new Error('Course ID required for update');
      }
      endpoint = `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.updateVideoCourseExcel}/${courseIdToUse}`;
      console.log('Using UPDATE endpoint for course:', courseIdToUse);
    } else {
      
      endpoint = `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.uploadVideoCourseDetails}`;
      if (existingCourseId) {
        formData.append('courseId', existingCourseId);
      }
      console.log('Using CREATE endpoint');
    }

    const response = await axios.post(
      endpoint,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${idToken}`
        },
        timeout: 60000
      }
    );

    let courseId;
    let responseData = response.data;

  
    if (typeof responseData === 'string') {
      const match = responseData.match(/successfully : (.+)$/);
      courseId = match ? match[1].trim() : null;
    } else if (responseData.courseId) {
      courseId = responseData.courseId;
    } else if (responseData.message) {
      const match = responseData.message.match(/successfully : (.+)$/);
      courseId = match ? match[1].trim() : null;
    }

    if (!courseId && existingCourseId) {
      courseId = existingCourseId;
    }

    if (!courseId) {
      throw new Error('Could not extract courseId from response');
    }

   
    setCourseIdFromApi(courseId);
    
  
const { fromEditOptions, updateType, redirectTo } = editFlowRef.current;

if (fromEditOptions && updateType === 'excelOnly') {
  showMessage('success', 'Excel updated successfully!');

  setTimeout(() => {
    navigate(redirectTo, {
      replace: true,
      state: {
        refresh: true,
        courseId
      }
    });
  }, 500);

  return; 
}

if (isUpdate) {
  showMessage('success', 'Excel file updated successfully!');
  
  
  if (editMode) {
  
    setTimeout(() => {
      navigate('/MHMV-profile/video-courses', {
        state: { 
          refresh: true,
          courseId: courseId,
          showSuccess: true,
          message: 'Excel updated successfully',
          fromExcelUpdate: true
        }
      });
    }, 1000);
  } else if (replaceMode) {
  
    setTimeout(() => {
      navigate(`/MHMV-profile/upload-course-videos/${courseId}`, {
        state: { 
          editMode: false,
          courseId: courseId,
          fromExcelUpdate: true,
          excelUpdated: true,
          refreshData: true
        }
      });
    }, 1000);
  } else if (excelAlreadyUploaded) {
   
    setTimeout(() => {
      navigate(`/MHMV-profile/upload-course-videos/${courseId}`, {
        state: { 
          editMode: false,
          courseId: courseId,
          fromExcelUpdate: true,
          excelUpdated: true,
          refreshData: true
        }
      });
    }, 1000);
  }
} else {
 
  showMessage('success', 'Course structure created successfully!');
  
  setTimeout(() => {
    navigate(`/MHMV-profile/upload-course-videos/${courseId}`, {
      state: { 
        editMode: editMode,
        courseId: courseId,
        fromExcelPage: true
      }
    });
  }, 1000);
}
  } catch (error) {
    console.error('Upload error:', error);
    let errorMsg = editMode || replaceMode ? 'Update failed. Please try again.' : 'Upload failed. Please try again.';
    
    if (error.response?.status === 413) {
      errorMsg = 'File too large. Maximum 10MB allowed.';
    } else if (error.response?.status === 415) {
      errorMsg = 'Invalid file format. Please upload Excel files only (.xlsx or .xls).';
    } else if (error.response?.data) {
      errorMsg = typeof error.response.data === 'string' 
        ? error.response.data 
        : error.response.data.message || JSON.stringify(error.response.data);
    } else if (error.message) {
      errorMsg = error.message;
    }
    
    showMessage('error', errorMsg);
  } finally {
    setLoading(false);
  }
};
  const clearSelection = () => {
    setExcelFile(null);
    if (excelInputRef.current) {
      excelInputRef.current.value = '';
    }
    showMessage('info', 'File selection cleared');
  };


  const getCourseIdForNavigation = () => {
    return existingCourseId || courseIdFromApi;
  };

  const refreshCourseData = () => {
    if (existingCourseId) {
      setRefreshTrigger(prev => prev + 1);
      showMessage('info', 'Refreshing course data...');
    }
  };

  const renderInstructionsBanner = () => {
    if (activeStep === 'instructors' || editMode) return null;

    return (
      <div className="vce-instructions-banner">
        <div className="vce-banner-content">
          <div className="vce-banner-icon">
            <Info size={20} />
          </div>
          <div className="vce-banner-text">
            <strong>Note:</strong> Review the{' '}
            <button 
              className="vce-banner-link"
              onClick={() => handleStepClick('instructors')}
            >
              Complete Instructions
            </button>{' '}
            for best results.
          </div>
          <button 
            className="vce-banner-dismiss"
            onClick={() => setShowInstructionsBanner(false)}
          >
            ×
          </button>
        </div>
      </div>
    );
  };

  const renderStepGuide = () => {
    if (!showStepGuide) return null;

    const steps = [
      {
        id: 'instructors',
        number: 1,
        title: "Complete Instructions",
        description: "Learn how to structure your course and upload videos",
        icon: Users,
        action: () => handleStepClick('instructors')
      },
      {
        id: 'uploadExcel',
        number: 2,
        title: excelAlreadyUploaded ? "Excel Uploaded ✓" : (editMode ? "Update Excel File" : "Upload Excel File"),
        description: excelAlreadyUploaded ? "Continue with video upload" : 
                    (editMode ? "Upload updated Excel file" : "Upload your structured Excel file here"),
        icon: excelAlreadyUploaded ? CheckCircle : Upload,
        action: () => handleStepClick('uploadExcel')
      }
    ];

    return (
      <div className="vce-step-guide-card">
        <div className="vce-step-guide-header">
          <div className="vce-step-guide-title">
            <Sparkles size={20} />
            <h3>{editMode ? 'Update Course' : 'How to Create Your Course'}</h3>
          </div>
          <button
            className="vce-step-guide-toggle"
            onClick={() => setShowStepGuide(false)}
            aria-label="Close guide"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        
        <div className="vce-steps-container">
          {steps.map((step, index) => (
            <div 
              key={step.id} 
              className={`vce-step-item ${activeStep === step.id ? 'active' : ''}`}
              onClick={step.action}
            >
              <div className="vce-step-number">
                {step.number}
              </div>
              <div className="vce-step-content">
                <h4>{step.title}</h4>
                <p>{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className="vce-step-connector"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderInstructorsSection = () => {
    if (activeStep !== 'instructors') return null;

    return (
      <div className="vce-instructors-section">
        <div className="vce-section-header">
          <div className="vce-section-title">
            <Users size={24} />
            <h2>Complete Instructions: How to Create Video Course</h2>
          </div>
          <button
            className="vce-section-toggle"
            onClick={() => setInstructorsExpanded(!instructorsExpanded)}
          >
            {instructorsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>

        {instructorsExpanded && (
          <div className="vce-instructors-content">
            {/* Step 1: Download Template */}
            <div className="vce-guide-step">
              <div className="vce-step-header">
                <div className="vce-step-number-large">1</div>
                <h3>Download Excel Template</h3>
              </div>
              <div className="vce-step-content">
                <p>Start by downloading our pre-formatted Excel template that ensures proper course structure:</p>
                <div className="vce-template-card">
                  <div className="vce-template-header">
                    <FileCode size={20} />
                    <div>
                      <h4>Video Course Template</h4>
                      <p>Structured Excel sheet with proper formatting</p>
                    </div>
                  </div>
                  <div className="vce-template-details">
                    <ul>
                      <li>✓ Structured columns for organized course setup</li>
                      <li>Ready-to-use format with examples included</li>
                      <li>✓ Guaranteed compatibility with our upload system</li>
                      <li>✓ Simple to fill - just replace example text</li>
                    </ul>
                    <button
                      className="vce-download-template-btn"
                      onClick={() => window.open('/templates/VideoCourseTemplate.xlsx', '_blank')}
                    >
                      <Download size={18} />
                      Download Excel Template
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Excel Structure */}
            <div className="vce-guide-step">
              <div className="vce-step-header">
                <div className="vce-step-number-large">2</div>
                <h3>Excel Sheet Structure</h3>
              </div>
              <div className="vce-step-content">
                <p>Fill the Excel sheet with this exact structure:</p>
                
                <div className="vce-excel-structure-guide">
                  <div className="vce-excel-table">
                    <div className="vce-table-header">
                      <div className="vce-table-row">
                        <div className="vce-table-cell vce-header-cell">Column</div>
                        <div className="vce-table-cell vce-header-cell">Description</div>
                        <div className="vce-table-cell vce-header-cell">Example</div>
                      </div>
                    </div>
                    <div className="vce-table-body">
                      <div className="vce-table-row">
                        <div className="vce-table-cell"><strong>Course Title</strong></div>
                        <div className="vce-table-cell">Name of your course</div>
                        <div className="vce-table-cell vce-example">Learn Python Programming</div>
                      </div>
                      <div className="vce-table-row">
                        <div className="vce-table-cell"><strong>Module Title</strong></div>
                        <div className="vce-table-cell">Section/Module name</div>
                        <div className="vce-table-cell vce-example">Introduction to Python</div>
                      </div>
                      <div className="vce-table-row">
                        <div className="vce-table-cell"><strong>Submodule Title</strong></div>
                        <div className="vce-table-cell">Lesson/Submodule name</div>
                        <div className="vce-table-cell vce-example">Setting Up Environment</div>
                      </div>
                      <div className="vce-table-row">
                        <div className="vce-table-cell"><strong>Video Title</strong></div>
                        <div className="vce-table-cell">Name of video file</div>
                        <div className="vce-table-cell vce-example">Python Installation Guide</div>
                      </div>
                      <div className="vce-table-row">
                        <div className="vce-table-cell"><strong>Description</strong></div>
                        <div className="vce-table-cell">Brief description</div>
                        <div className="vce-table-cell vce-example">Learn how to install Python</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="vce-tips-box">
                  <h4><HelpCircle size={16} /> Critical Requirements:</h4>
                  <ul>
                    <li><strong>Video Titles Must Match Filenames Exactly:</strong></li>
                    <li>Example: If video title is "Python Installation", name your file exactly "Python Installation.mp4"</li>
                    <li>Do NOT add extra words like "my", "video", "lecture" before the title</li>
                    <li>Maintain proper hierarchy: Course → Module → Submodule → Video</li>
                    <li>Remove special characters from filenames</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step 3: Video Instructions */}
            <div className="vce-guide-step">
              <div className="vce-step-header">
                <div className="vce-step-number-large">3</div>
                <h3>Upload Process</h3>
              </div>
              <div className="vce-step-content">
                <p>Follow these steps to upload your video course:</p>
                
                <div className="vce-video-instructions">
                  <div className="vce-instruction-card">
                    <div className="vce-instruction-icon">
                      <FileText size={24} />
                    </div>
                    <div className="vce-instruction-content">
                      <h4>Step 1: Fill Excel Template</h4>
                      <p>Download our template and fill all course details including modules, submodules, and video titles.</p>
                      <p className="vce-note"><strong>Important:</strong> Video titles in Excel must match your video filenames exactly.</p>
                    </div>
                  </div>

                  <div className="vce-instruction-card">
                    <div className="vce-instruction-icon">
                      <Upload size={24} />
                    </div>
                    <div className="vce-instruction-content">
                      <h4>Step 2: Upload Excel File</h4>
                      <p>Upload the completed Excel file using the upload section on this page.</p>
                    </div>
                  </div>

                  <div className="vce-instruction-card">
                    <div className="vce-instruction-icon">
                      <Video size={24} />
                    </div>
                    <div className="vce-instruction-content">
                      <h4>Step 3: Upload Video Files</h4>
                      <p>After Excel upload, you'll be redirected to upload matching video files.</p>
                      <p className="vce-note"><strong>Critical:</strong> Video filenames must match the titles in Excel exactly.</p>
                    </div>
                  </div>
                </div>

                {/* Video Demonstration */}
                <div className="vce-video-demo-section">
                  <h4><PlayCircle size={18} /> Watch Video Demonstration</h4>
                  <div className="vce-video-placeholder">
                    <div className="vce-video-thumbnail">
                      <PlayCircle size={48} />
                      <p>How to Upload Video Course - Step by Step Guide</p>
                    </div>
                    <button className="vce-watch-video-btn">
                      <PlayCircle size={16} />
                      Play Tutorial Video
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Step */}
            <div className="vce-next-step-section">
              <div className="vce-next-step-card">
                <div className="vce-next-step-content">
                  <h4>Ready to Upload Excel?</h4>
                  <p>Now that you understand the process, click below to upload your Excel file.</p>
                  <button
                    className="vce-upload-excel-btn"
                    onClick={() => handleStepClick('uploadExcel')}
                  >
                    <Upload size={18} />
                    Go to Excel Upload
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderUploadSection = () => {
    if (activeStep !== 'uploadExcel') return null;

    const currentCourseId = getCourseIdForNavigation();
    const isUpdateFlow = replaceMode || editMode || excelAlreadyUploaded;
    
    return (
      <>
        {/* Existing Excel File Info Card - Show when Excel already exists and NOT in replace mode */}
        {excelAlreadyUploaded && existingExcelFile && !replaceMode && (
          <div className="vce-existing-file-section">
            <div className="vce-existing-file-card">
              <div className="vce-existing-file-header">
                <FileSpreadsheet size={24} />
                <div className="vce-existing-file-title">
                  <h3>Existing Excel File</h3>
                  <p>You can download and update this file</p>
                </div>
                <button 
                  className="vce-refresh-data-btn"
                  onClick={refreshCourseData}
                  title="Refresh course data"
                >
                  <RefreshCw size={16} />
                </button>
              </div>
              
              <div className="vce-existing-file-content">
                <div className="vce-file-info-display">
                  <div className="vce-file-icon-name">
                    <FileSpreadsheet size={20} />
                    <div className="vce-file-details">
                      <span className="vce-file-name">{existingExcelFile.name}</span>
                      <span className="vce-file-meta">
                        {existingExcelFile.size} • Uploaded on {existingExcelFile.uploadedAt}
                      </span>
                    </div>
                  </div>
                  <button
                    className="vce-download-excel-btn"
                    onClick={downloadExistingExcel}
                    disabled={!existingExcelFile.filePath}
                    title={existingExcelFile.filePath ? 
                      `Download ${existingExcelFile.name}` : 
                      "File not available"}
                  >
                    <Download size={16} />
                    Download Excel
                  </button>
                </div>
                
                <div className="vce-file-note">
                  <Info size={14} />
                  <span>
                    You can download this file, make changes, and re-upload it to update your course structure.
                    Make sure video titles in the Excel file match your video filenames exactly.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Course Status Card - Only show if Excel already uploaded and NOT in replace mode or edit mode */}
        {excelAlreadyUploaded && !replaceMode && !editMode && (
          <div className="vce-course-status-card vce-status-active">
            <div className="vce-status-card-header">
              <CheckCircle size={32} />
              <div className="vce-status-card-title">
                <h3>Course Structure Ready</h3>
                <p>Your Excel file has been uploaded successfully</p>
                {courseModules.length > 0 && (
                  <p className="vce-modules-count">
                    Found {courseModules.length} module(s) with {courseModules.reduce((total, mod) => total + (mod.submodules?.length || mod.videoSubModules?.length || 0), 0)} lessons
                  </p>
                )}
              </div>
            </div>
            <div className="vce-status-card-actions">
              {/* Continue to Video Upload Button */}
              <button
                className="vce-continue-button"
                onClick={() => {
                  if (!currentCourseId) {
                    showMessage('error', 'Course ID not found. Please refresh the page.');
                    return;
                  }
                  
                  console.log('Navigating to video upload with courseId:', currentCourseId);
                  
                  navigate(`/MHMV-profile/upload-course-videos/${currentCourseId}`, {
                    state: { 
                      editMode: editMode,
                      courseId: currentCourseId,
                      fromExcelPage: true,
                      excelAlreadyUploaded: true,
                      refreshData: true
                    }
                  });
                }}
              >
                Continue to Video Upload
                <ArrowRight size={18} />
              </button>
              
              <button
                className="vce-replace-button"
                onClick={handleReplaceClick}
              >
                Replace Excel File
              </button>
              
              {/* Back to Video Upload button when coming from video upload */}
              {fromVideoUpload && (
                <button
                  className="vce-back-to-videos-button"
                  onClick={() => {
                    if (!currentCourseId) {
                      showMessage('error', 'Course ID not found');
                      return;
                    }
                    
                    navigate(`/MHMV-profile/upload-course-videos/${currentCourseId}`, {
                      state: { 
                        editMode: editMode,
                        courseId: currentCourseId,
                        fromExcelPage: true,
                        refreshData: true
                      }
                    });
                  }}
                >
                  <ArrowLeft size={18} />
                  Back to Video Upload
                </button>
              )}
            </div>
          </div>
        )}

      {(excelAlreadyUploaded || replaceMode || editMode) && (
          <div className="vce-upload-card">
            <div className="vce-upload-card-header">
              <FileSpreadsheet size={24} />
              <div className="vce-upload-card-title">
                <h2>
                  {replaceMode ? 'Replace Excel File' : 
                   editMode ? 'Update Course Excel File' : 
                   excelAlreadyUploaded ? 'Update Course Structure' : 'Upload Course Excel File'}
                </h2>
                <p>
                  {replaceMode 
                    ? 'Upload a new Excel file to replace existing structure' 
                    : editMode 
                    ? 'Upload updated Excel file to modify course structure' 
                    : excelAlreadyUploaded
                    ? 'Update your course structure with new Excel file'
                    : 'Upload your structured Excel file with course details'}
                </p>
                {isUpdateFlow && currentCourseId && (
                  <p className="vce-update-note">
                    <Info size={14} /> This will update the existing course: <strong>{currentCourseId}</strong>
                  </p>
                )}
              </div>
            </div>

            <div className="vce-upload-card-body">
              {/* Show upload zone */}
              <>
                {/* UPLOAD ZONE */}
                <div className="vce-upload-zone">
                  <div className="vce-upload-zone-icon">
                    <FolderOpen size={48} />
                  </div>
                  
                  <div className="vce-upload-zone-content">
                    <h3>Drag & Drop or Browse</h3>
                    <p>Upload Excel file (.xlsx, .xls) up to 10MB</p>
                    
                    <input
                      type="file"
                      ref={excelInputRef}
                      accept=".xlsx,.xls"
                      onChange={handleExcelFileSelect}
                      className="vce-file-input"
                      id="excel-upload"
                    />
                    
                    <label htmlFor="excel-upload" className="vce-upload-button">
                      <Upload size={18} />
                      {replaceMode ? 'Choose New Excel File' : 
                       editMode ? 'Choose Updated Excel File' : 
                       excelAlreadyUploaded ? 'Choose Updated Excel File' : 'Choose Excel File'}
                    </label>
                    
                    <div className="vce-file-requirements">
                      <span>Requirements:</span>
                      <ul>
                        <li>Excel format (.xlsx or .xls)</li>
                        <li>Maximum file size: 10MB</li>
                        <li>Follow template structure exactly</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Selected File Preview */}
                {excelFile && (
                  <div className="vce-selected-file-preview">
                    <div className="vce-file-preview-header">
                      <FileSpreadsheet size={20} />
                      <h4>Selected File</h4>
                    </div>
                    <div className="vce-file-preview-content">
                      <div className="vce-file-info">
                        <span className="vce-file-name">{excelFile.name}</span>
                        <span className="vce-file-size">
                          {(excelFile.size / 1024).toFixed(0)} KB
                        </span>
                      </div>
                      <button
                        onClick={clearSelection}
                        className="vce-remove-file-button"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}

                {/* Template Download */}
                <div className="vce-template-section">
                  <div className="vce-template-header">
                    <Download size={18} />
                    <h4>Need the template?</h4>
                  </div>
                  <p>Get the Excel template here and fill your course details easily.</p>
                  <button
                    className="vce-template-download-button"
                    onClick={() => handleStepClick('instructors')}
                  >
                    <Download size={16} />
                    Get Excel Template
                  </button>
                </div>
              </>
            </div>
          </div>
        )}
      </>
    );
  };

  if (!authChecked) {
    return (
      <div className="vce-loading-overlay">
        <div className="vce-spinner"></div>
        <p>Initializing...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="vce-auth-required-container">
        <div className="vce-auth-required-card">
          <Shield size={48} />
          <h2>Authentication Required</h2>
          <p>Please sign in to create and manage video courses</p>
          <button
            onClick={() => navigate('/MHMV-profile/jobseeker-login')}
            className="vce-auth-button"
          >
            Sign In to Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="vce-container">
      {/* Header */}
      <header className="vce-upload-header">
        <div className="vce-container-inner">
          <div className="vce-header-content">
            <div className="vce-header-brand">
              <button
                className="vce-navigation-button"
                onClick={() => navigate('/MHMV-profile/video-courses')}
                aria-label="Go back to courses"
              >
                <Home size={20} />
                <span>Courses</span>
              </button>
              <div className="vce-header-titles">
                <h1>
                  {replaceMode ? 'Replace Excel File' : 
                   editMode ? 'Update Video Course' : 
                   excelAlreadyUploaded ? 'Update Course Structure' : 'Create Video Course'}
                </h1>
                <p className="vce-header-subtitle">
                  {replaceMode ? 'Upload new Excel file for existing course' : 
                   editMode ? 'Step 1: Update Course Structure' : 
                   excelAlreadyUploaded ? 'Update your course Excel structure' : 'Step 1: Upload Course Structure'}
                </p>
              </div>
            </div>
            
            <div className="vce-user-profile">
              <div className="vce-user-avatar">
                {user.email?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="vce-user-info">
                <span className="vce-user-name">{user.email?.split('@')[0] || 'User'}</span>
                <span className="vce-user-role">Course Creator</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="vce-upload-main-content">
        <div className="vce-main-content-wrapper">
         
          {message.text && (
            <div className={`vce-status-alert vce-status-${message.type}`}>
              <div className="vce-alert-content">
                {message.type === 'success' && <CheckCircle size={18} />}
                {message.type === 'error' && <XCircle size={18} />}
                {message.type === 'info' && <AlertCircle size={18} />}
                <span>{message.text}</span>
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="vce-upload-content-grid">
            
            <div className="vce-upload-sidebar">
              {renderStepGuide()}
              
              
              <div className="vce-tips-card">
                <div className="vce-tips-header">
                  <HelpCircle size={20} />
                  <h3>Quick Tips</h3>
                </div>
                <div className="vce-tips-content">
                  <div className="vce-tip-item">
                    <div className="vce-tip-bullet">✓</div>
                    <div className="vce-tip-text">Use our template for proper formatting</div>
                  </div>
                  <div className="vce-tip-item">
                    <div className="vce-tip-bullet">✓</div>
                    <div className="vce-tip-text">Video titles in Excel must match file names exactly</div>
                  </div>
                  <div className="vce-tip-item">
                    <div className="vce-tip-bullet">✓</div>
                    <div className="vce-tip-text">Click on steps to get detailed instructions</div>
                  </div>
                  {editMode && (
                    <div className="vce-tip-item">
                      <div className="vce-tip-bullet">✓</div>
                      <div className="vce-tip-text">Existing videos are preserved if titles match</div>
                    </div>
                  )}
                  {(replaceMode || excelAlreadyUploaded) && (
                    <div className="vce-tip-item">
                      <div className="vce-tip-bullet">✓</div>
                      <div className="vce-tip-text">Replacing Excel updates the same course with new structure</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Dynamic Content */}
            <div className="vce-upload-main-area">
              {activeStep === 'instructors' && renderInstructorsSection()}
              {activeStep === 'uploadExcel' && renderUploadSection()}
            </div>
          </div>

          {/* Action Footer - Show when in uploadExcel step AND upload card is visible */}
          {activeStep === 'uploadExcel' && (!excelAlreadyUploaded || replaceMode || editMode) && (
            <div className="vce-upload-actions">
              <div className="vce-left-actions">
                {/* Cancel button shows in replace mode or edit mode */}
                {(replaceMode || editMode) && (
                  <button
                    onClick={handleCancelReplace}
                    className="vce-cancel-action-button"
                    disabled={loading}
                  >
                    <X size={18} />
                    {editMode ? 'Cancel Edit' : 'Cancel Replace'}
                  </button>
                )}
                <button
                  onClick={clearSelection}
                  className="vce-secondary-action-button"
                  disabled={loading || !excelFile}
                >
                  Clear File
                </button>
              </div>
              
              <div className="vce-primary-action-group">
                {excelFile && (
                  <div className="vce-upload-status">
                    <span className="vce-file-ready">
                      ✓ File ready for {replaceMode ? 'replacement' : editMode ? 'update' : 'upload'}
                    </span>
                    {(replaceMode || excelAlreadyUploaded) && (
                      <span className="vce-update-warning">
                        <AlertCircle size={14} /> This will update existing course structure
                      </span>
                    )}
                  </div>
                )}
                
                <button
                  onClick={handleUpload}
                  className="vce-primary-action-button"
                  disabled={loading || !excelFile}
                >
                  {loading ? (
                    <>
                      <span className="vce-button-spinner"></span>
                      {replaceMode ? 'Replacing...' : editMode ? 'Updating...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      {replaceMode ? 'Replace & Continue' : 
                       editMode ? 'Update & Continue' : 
                       excelAlreadyUploaded ? 'Update & Continue' : 'Upload & Continue'}
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default VideoCourseExcelUpload;